import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ApiClient from '../utils/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, X, User, UserPlus } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PhoneInput from '../components/PhoneInput';
import QuickAssignModal from '../components/QuickAssignModal';
import { LANGUAGES } from '../utils/languages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service Settings (Modality)
const SERVICE_SETTINGS = [
  { value: 'onsite', label: 'On-Site (In-Person)' },
  { value: 'opi', label: 'OPI (Over-the-Phone)' },
  { value: 'vri', label: 'VRI (Video Remote)' },
];

// Service Categories for Interpreting
const SERVICE_CATEGORIES = [
  { value: 'medical', label: 'Medical', desc: 'Clinics, hospitals, therapy, discharge' },
  { value: 'legal', label: 'Legal', desc: 'USCIS, hearings, depositions, attorney-client' },
  { value: 'community', label: 'Community', desc: 'Schools, social services, housing, benefits' },
  { value: 'business', label: 'Business', desc: 'HR meetings, trainings, safety, employee relations' },
  { value: 'conference', label: 'Conference', desc: 'Panels, meetings, public forums' },
];

// Legal Billing Options
const LEGAL_BILLING_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'half_day', label: 'Half Day (4 hours)' },
  { value: 'full_day', label: 'Full Day (8 hours)' },
];

const WorkOrderForm = () => {
  const { workOrderId } = useParams();
  const isEdit = !!workOrderId;
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [serviceCategories, setServiceCategories] = useState({});
  const [isViewMode, setIsViewMode] = useState(false);
  const [quickAssignOpen, setQuickAssignOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    work_order_type: 'interpreting',
    client_id: '',
    target_language: '',  // Single target language for interpreting
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    languages: [],  // Legacy field
    service_category: '',
    service_subcategory: '',
    instructions: '',
    special_requirements: '',
    deadline: '',
    // For display in view mode
    assigned_vendor_id: null,
    assigned_vendor_name: null,
    status: 'new_request',
    interpreting_details: {
      modality: 'onsite',
      service_category: 'medical',
      start_datetime: '',
      end_datetime: '',
      expected_duration: '',  // Client's expected duration
      timezone: 'America/Chicago',
      location: '',
      location_details: '',  // Suite, floor, etc.
      remote_link: '',
      legal_billing_type: '',  // hourly, half_day, full_day
      legal_billing_units: 1,  // Number of units
      team_required: false,
      min_hours: 2,
      rounding_rule: 15
    },
    translation_details: {
      source_language: 'en',
      target_languages: [],
      word_count: 0,
      file_types: [],
      workflow_stages: ['translate', 'edit', 'proof', 'qa', 'delivery'],
      stage_due_dates: {},
      current_stage: 'translate'
    }
  });

  useEffect(() => {
    fetchClients();
    fetchServiceCategories();
    if (isEdit) {
      fetchWorkOrder();
      // Check if we're in view mode (not edit mode)
      setIsViewMode(!window.location.pathname.includes('/edit'));
    }
  }, [workOrderId]);

  const fetchServiceCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/service-categories`);
      if (response.ok) {
        const data = await response.json();
        setServiceCategories(data);
      }
    } catch (error) {
      console.error('Failed to load service categories');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const fetchWorkOrder = async () => {
    try {
      const response = await fetch(`${API_URL}/api/work-orders/${workOrderId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(data);
      }
    } catch (error) {
      toast.error('Failed to load work order');
    }
  };

  // Handle unassigning a vendor from the work order
  const handleUnassign = async () => {
    if (!formData.assignment_id) {
      toast.error('No assignment found to unassign');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to unassign ${formData.assigned_vendor_name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/assignments/${formData.assignment_id}?reason=Unassigned by admin`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(`${formData.assigned_vendor_name} has been unassigned`);
        // Refresh the work order data
        fetchWorkOrder();
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to unassign vendor');
      }
    } catch (error) {
      console.error('Unassign error:', error);
      toast.error('Failed to unassign vendor');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Client-side validation
    if (!formData.client_id) {
      toast.error('Please select a client', { duration: 5000 });
      setLoading(false);
      return;
    }
    
    if (formData.work_order_type === 'interpreting') {
      if (!formData.target_language) {
        toast.error('Please select a target language', { duration: 5000 });
        setLoading(false);
        return;
      }
      if (!formData.interpreting_details?.start_datetime) {
        toast.error('Please set a start date/time', { duration: 5000 });
        setLoading(false);
        return;
      }
    }
    
    try {
      const payload = { ...formData };
      // Clear type-specific data
      if (formData.work_order_type === 'interpreting') {
        payload.translation_details = null;
      } else {
        payload.interpreting_details = null;
      }
      
      const url = isEdit 
        ? `/api/work-orders/${workOrderId}` 
        : `/api/work-orders`;
      
      const headers = getAuthHeaders();
      const result = isEdit 
        ? await ApiClient.put(url, payload, headers)
        : await ApiClient.post(url, payload, headers);
      
      if (result.ok) {
        toast.success(isEdit ? 'Work order updated successfully!' : 'Work order created successfully!');
        navigate('/work-orders');
      } else {
        const errors = ApiClient.parseError(result.data, 'Failed to save work order');
        if (Array.isArray(errors)) {
          errors.forEach(err => toast.error(err, { duration: 5000 }));
        } else {
          toast.error(errors, { duration: 5000 });
        }
      }
    } catch (error) {
      console.error('Work order save error:', error);
      toast.error(`Failed to save work order: ${error.message || 'Network error'}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = (code) => {
    setFormData({
      ...formData,
      languages: formData.languages.includes(code)
        ? formData.languages.filter(l => l !== code)
        : [...formData.languages, code]
    });
  };

  const updateInterpretingDetails = (field, value) => {
    setFormData({
      ...formData,
      interpreting_details: { ...formData.interpreting_details, [field]: value }
    });
  };

  const updateTranslationDetails = (field, value) => {
    setFormData({
      ...formData,
      translation_details: { ...formData.translation_details, [field]: value }
    });
  };

  const toggleTargetLanguage = (code) => {
    const current = formData.translation_details.target_languages || [];
    setFormData({
      ...formData,
      translation_details: {
        ...formData.translation_details,
        target_languages: current.includes(code)
          ? current.filter(l => l !== code)
          : [...current, code]
      }
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-8" data-testid="work-order-form">
      {/* Header */}
      <div className="flex items-center gap-4 bg-white sticky top-0 z-10 py-4 border-b">
        <Button variant="ghost" size="icon" onClick={() => navigate('/work-orders')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-gray-900">
            {isEdit ? 'Edit Work Order' : 'New Work Order'}
          </h1>
          <p className="text-sm text-gray-500">Fill in the details below to create a new work order</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Work Order Type */}
        <Card className="border-2 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-sm">1</span>
              </div>
              Work Order Type
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 gap-4">
              <Button
                type="button"
                variant={formData.work_order_type === 'interpreting' ? 'default' : 'outline'}
                className={`h-20 flex-col gap-1 transition-all ${
                  formData.work_order_type === 'interpreting' 
                    ? 'bg-blue-600 hover:bg-blue-700 ring-2 ring-blue-600 ring-offset-2' 
                    : 'hover:border-blue-300 hover:bg-blue-50'
                }`}
                onClick={() => setFormData({ ...formData, work_order_type: 'interpreting' })}
                data-testid="type-interpreting"
              >
                <div className="font-semibold text-base">Interpreting</div>
                <div className="text-xs opacity-80">Scheduled assignments</div>
              </Button>
              <Button
                type="button"
                variant={formData.work_order_type === 'translation' ? 'default' : 'outline'}
                className={`h-20 flex-col gap-1 transition-all ${
                  formData.work_order_type === 'translation' 
                    ? 'bg-purple-600 hover:bg-purple-700 ring-2 ring-purple-600 ring-offset-2' 
                    : 'hover:border-purple-300 hover:bg-purple-50'
                }`}
                onClick={() => setFormData({ ...formData, work_order_type: 'translation' })}
                data-testid="type-translation"
              >
                <div className="font-semibold text-base">Translation</div>
                <div className="text-xs opacity-80">Document projects</div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Client & Requester */}
        <Card className="border-2 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-sm">2</span>
              </div>
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              >
                <SelectTrigger data-testid="client-select">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="requester_name">Requester Name</Label>
                <Input
                  id="requester_name"
                  value={formData.requester_name}
                  onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })}
                  data-testid="requester-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="requester_email">Requester Email</Label>
                <Input
                  id="requester_email"
                  type="email"
                  value={formData.requester_email}
                  onChange={(e) => setFormData({ ...formData, requester_email: e.target.value })}
                  data-testid="requester-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="requester_phone">Requester Phone</Label>
                <PhoneInput
                  id="requester_phone"
                  value={formData.requester_phone}
                  onChange={(value) => setFormData(prev => ({ ...prev, requester_phone: value }))}
                  data-testid="requester-phone"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Category */}
        <Card className="border-2 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-sm">3</span>
              </div>
              Service Category
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service_category">Category *</Label>
                <Select 
                  value={formData.service_category} 
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    service_category: value,
                    service_subcategory: '' // Reset subcategory when category changes
                  })}
                >
                  <SelectTrigger data-testid="service-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceCategories[formData.work_order_type]?.categories?.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Subcategory - only show if category has subcategories */}
              {formData.service_category && 
               serviceCategories[formData.work_order_type]?.categories
                 ?.find(c => c.value === formData.service_category)?.subcategories?.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="service_subcategory">Subcategory</Label>
                  <Select 
                    value={formData.service_subcategory} 
                    onValueChange={(value) => setFormData({ ...formData, service_subcategory: value })}
                  >
                    <SelectTrigger data-testid="service-subcategory-select">
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceCategories[formData.work_order_type]?.categories
                        ?.find(c => c.value === formData.service_category)?.subcategories
                        ?.map((sub) => (
                          <SelectItem key={sub.value} value={sub.value}>
                            {sub.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Languages - Single Target Language for Interpreting */}
        <Card className="border-2 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold text-sm">4</span>
              </div>
              Language
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            {formData.work_order_type === 'interpreting' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="source_language">Source Language</Label>
                  <Select 
                    value={formData.interpreting_details?.source_language || 'en'} 
                    onValueChange={(value) => updateInterpretingDetails('source_language', value)}
                  >
                    <SelectTrigger data-testid="source-language-select">
                      <SelectValue placeholder="Select source language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      {LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="target_language">Target Language *</Label>
                  <Select 
                    value={formData.target_language} 
                    onValueChange={(value) => {
                      setFormData({ 
                        ...formData, 
                        target_language: value,
                        languages: [value]  // Keep legacy field in sync
                      });
                    }}
                  >
                    <SelectTrigger data-testid="target-language-select">
                      <SelectValue placeholder="Select target language" />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Label>Target Languages *</Label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map((lang) => (
                    <Badge
                      key={lang.code}
                      variant={formData.languages.includes(lang.code) ? 'default' : 'outline'}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => toggleLanguage(lang.code)}
                    >
                      {lang.name}
                      {formData.languages.includes(lang.code) && (
                        <X className="h-3 w-3 ml-1" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assigned Interpreter - Display only in View Mode when assigned */}
        {isViewMode && formData.assigned_vendor_name && (
          <Card className="border-green-200 bg-green-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-800">
                <User className="h-5 w-5" />
                Assigned Interpreter
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <User className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{formData.assigned_vendor_name}</p>
                    <Badge className="bg-green-100 text-green-800">Assigned</Badge>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => setQuickAssignOpen(true)}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    data-testid="reassign-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Reassign
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => handleUnassign()}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="unassign-btn"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Unassign
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Assign Interpreter Button - Show when no vendor assigned and in view mode */}
        {isViewMode && !formData.assigned_vendor_id && formData.work_order_type === 'interpreting' && (
          <Card className="border-orange-200 bg-orange-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <UserPlus className="h-5 w-5" />
                Interpreter Assignment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-700 font-medium">No interpreter assigned yet</p>
                  <p className="text-sm text-orange-600">Assign a qualified interpreter to this work order</p>
                </div>
                <Button 
                  onClick={() => setQuickAssignOpen(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="assign-interpreter-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign Interpreter
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Assign Translator Button - Show when no vendor assigned for translation work orders */}
        {isViewMode && !formData.assigned_vendor_id && formData.work_order_type === 'translation' && (
          <Card className="border-orange-200 bg-orange-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <UserPlus className="h-5 w-5" />
                Translator Assignment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-700 font-medium">No translator assigned yet</p>
                  <p className="text-sm text-orange-600">Assign a qualified translator to this work order</p>
                </div>
                <Button 
                  onClick={() => setQuickAssignOpen(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="assign-translator-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign Translator
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Interpreting Details */}
        {formData.work_order_type === 'interpreting' && (
          <Card className="border-2 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-white border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">5</span>
                </div>
                Interpreting Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              {/* Service Setting (Modality) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service Setting *</Label>
                  <Select 
                    value={formData.interpreting_details.modality} 
                    onValueChange={(value) => updateInterpretingDetails('modality', value)}
                  >
                    <SelectTrigger data-testid="service-setting-select">
                      <SelectValue placeholder="Select setting" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_SETTINGS.map((setting) => (
                        <SelectItem key={setting.value} value={setting.value}>
                          {setting.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Service Category *</Label>
                  <Select 
                    value={formData.interpreting_details.service_category} 
                    onValueChange={(value) => {
                      updateInterpretingDetails('service_category', value);
                      // Clear legal billing if not legal
                      if (value !== 'legal') {
                        updateInterpretingDetails('legal_billing_type', '');
                        updateInterpretingDetails('legal_billing_units', 1);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="interp-service-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex flex-col">
                            <span>{cat.label}</span>
                            <span className="text-xs text-muted-foreground">{cat.desc}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Schedule */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date/Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.interpreting_details.start_datetime?.slice(0, 16) || ''}
                    onChange={(e) => updateInterpretingDetails('start_datetime', e.target.value)}
                    data-testid="start-datetime"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date/Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.interpreting_details.end_datetime?.slice(0, 16) || ''}
                    onChange={(e) => updateInterpretingDetails('end_datetime', e.target.value)}
                    data-testid="end-datetime"
                  />
                </div>
              </div>
              
              {/* Expected Duration */}
              <div className="space-y-2">
                <Label>Expected Duration</Label>
                <Input
                  placeholder="e.g., 2 hours, 30 minutes, half day"
                  value={formData.interpreting_details.expected_duration || ''}
                  onChange={(e) => updateInterpretingDetails('expected_duration', e.target.value)}
                  data-testid="expected-duration"
                />
                <p className="text-xs text-muted-foreground">Client&apos;s estimated length of service</p>
              </div>

              {/* Legal Billing Section - Only show when category is Legal */}
              {formData.interpreting_details.service_category === 'legal' && (
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-amber-800">Legal Billing</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Billing Type</Label>
                        <Select 
                          value={formData.interpreting_details.legal_billing_type || ''} 
                          onValueChange={(value) => updateInterpretingDetails('legal_billing_type', value)}
                        >
                          <SelectTrigger data-testid="legal-billing-type-select">
                            <SelectValue placeholder="Select billing type" />
                          </SelectTrigger>
                          <SelectContent>
                            {LEGAL_BILLING_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Number of Units</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.interpreting_details.legal_billing_units || 1}
                          onChange={(e) => updateInterpretingDetails('legal_billing_units', parseInt(e.target.value) || 1)}
                          data-testid="legal-billing-units"
                        />
                        <p className="text-xs text-muted-foreground">
                          {formData.interpreting_details.legal_billing_type === 'hourly' && 'Number of hours'}
                          {formData.interpreting_details.legal_billing_type === 'half_day' && 'Number of half-days (4h each)'}
                          {formData.interpreting_details.legal_billing_type === 'full_day' && 'Number of full days (8h each)'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Location / Remote Link */}
              {formData.interpreting_details.modality === 'onsite' ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Location Address *</Label>
                    <AddressAutocomplete
                      value={formData.interpreting_details.location}
                      onChange={(value) => updateInterpretingDetails('location', value)}
                      placeholder="Enter the full address"
                      data-testid="location-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location Details</Label>
                    <Input
                      placeholder="Suite, floor, room, building name, etc."
                      value={formData.interpreting_details.location_details || ''}
                      onChange={(e) => updateInterpretingDetails('location_details', e.target.value)}
                      data-testid="location-details"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Meeting Link</Label>
                  <Input
                    value={formData.interpreting_details.remote_link}
                    onChange={(e) => updateInterpretingDetails('remote_link', e.target.value)}
                    placeholder="https://zoom.us/j/..."
                  />
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Minimum Hours</Label>
                  <Select 
                    value={String(formData.interpreting_details.min_hours || 2)} 
                    onValueChange={(value) => updateInterpretingDetails('min_hours', parseInt(value))}
                  >
                    <SelectTrigger data-testid="min-hours-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 hour</SelectItem>
                      <SelectItem value="2">2 hours</SelectItem>
                      <SelectItem value="3">3 hours</SelectItem>
                      <SelectItem value="4">4 hours</SelectItem>
                      <SelectItem value="5">5 hours</SelectItem>
                      <SelectItem value="6">6 hours</SelectItem>
                      <SelectItem value="7">7 hours</SelectItem>
                      <SelectItem value="8">8 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 p-3 border rounded-lg mt-6">
                  <Switch
                    checked={formData.interpreting_details.team_required}
                    onCheckedChange={(checked) => updateInterpretingDetails('team_required', checked)}
                  />
                  <Label>Team Required (2+ interpreters)</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Translation Details */}
        {formData.work_order_type === 'translation' && (
          <Card className="border-2 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-white border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-sm">5</span>
                </div>
                Translation Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source Language</Label>
                  <Select 
                    value={formData.translation_details.source_language} 
                    onValueChange={(value) => updateTranslationDetails('source_language', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      {LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Word Count</Label>
                  <Input
                    type="number"
                    value={formData.translation_details.word_count}
                    onChange={(e) => updateTranslationDetails('word_count', parseInt(e.target.value))}
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <Label>Target Languages</Label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map((lang) => (
                    <Badge
                      key={lang.code}
                      variant={formData.translation_details.target_languages?.includes(lang.code) ? 'secondary' : 'outline'}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => toggleTargetLanguage(lang.code)}
                    >
                      {lang.name}
                      {formData.translation_details.target_languages?.includes(lang.code) && (
                        <X className="h-3 w-3 ml-1" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Delivery Deadline</Label>
                <Input
                  type="datetime-local"
                  value={formData.deadline?.slice(0, 16) || ''}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="border-2 shadow-sm">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-gray-600 font-bold text-sm">6</span>
              </div>
              Additional Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-2">
              <Label>Instructions</Label>
              <Textarea
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                rows={3}
                placeholder="Any specific instructions for this work order..."
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <Label>Special Requirements</Label>
              <Textarea
                value={formData.special_requirements}
                onChange={(e) => setFormData({ ...formData, special_requirements: e.target.value })}
                rows={2}
                placeholder="E.g., medical terminology, legal expertise..."
                className="resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3 justify-end sticky bottom-0 bg-white py-4 border-t -mx-4 px-4">
          <Button type="button" variant="outline" onClick={() => navigate('/work-orders')} className="min-w-[100px]">
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading || !formData.client_id} 
            data-testid="save-wo-btn"
            className="min-w-[180px] bg-blue-600 hover:bg-blue-700"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Work Order' : 'Create Work Order')}
          </Button>
        </div>
      </form>
      
      {/* Quick Assign Modal */}
      <QuickAssignModal
        open={quickAssignOpen}
        onOpenChange={setQuickAssignOpen}
        workOrder={formData}
        onAssigned={() => {
          // Refresh the work order data after assignment
          fetchWorkOrder();
        }}
      />
    </div>
  );
};

export default WorkOrderForm;
